// 📸 10-02 THE POSTCARD (Nina: something on the credits card "like a shareable artifact that people can use
// that can help us advertise claude house"). The event's group photo (`house.feedback.photo_url`: our content
// bucket only, checked server-side by eventFeedback.PHOTO_URL_RE) with a band under it: the event's name and
// the house link. Phones get the share sheet WITH the picture; everywhere else the postcard downloads and the
// link is copied. Never a broken share: if the photo will not load, the link still shares (shareLink).
//
// CORS (quoteCard.js measured it 07-19, re-read 10-02): the bucket answers `Access-Control-Allow-Origin: *`.
// Like quoteCard we fetch(mode:'cors') -> blob -> ImageBitmap, so a no-CORS <img> response already in the
// cache can never taint the canvas.
import { shareLink, CANONICAL_ORIGIN } from './shareLink';

export const POSTCARD_URL = `${CANONICAL_ORIGIN}/house`;
export const POSTCARD_SHARED = 'Shared!';
export const POSTCARD_SAVED = 'Saved. Link copied.';
export const POSTCARD_COPIED = 'Link copied.';
export const POSTCARD_FAILED = "Couldn't share. Try again?";

const WIDTH = 1600;
const BAND = 120;
const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

/** "apple picking" → "Apple picking at the Claude House". */
export const postcardTitle = (label) => {
  const l = String(label || '').trim();
  return l ? `${l.charAt(0).toUpperCase()}${l.slice(1)} at the Claude House` : 'The Claude House';
};

/** "apple picking" → "claude-house-apple-picking.png". */
export const postcardFileName = (label) => {
  const slug = String(label || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
  return `claude-house-${slug || 'night'}.png`;
};

async function loadBitmap(url) {
  const res = await fetch(url, { mode: 'cors' });
  if (!res.ok) throw new Error(`photo ${res.status}`);
  return createImageBitmap(await res.blob());
}

/** The postcard as a PNG blob, or null when anything fails (the caller falls back to the link). */
export async function composePostcard({ photoUrl, label } = {}) {
  try {
    if (!photoUrl) return null;
    const img = await loadBitmap(photoUrl);
    const h = Math.round((WIDTH * img.height) / img.width);
    const canvas = document.createElement('canvas');
    canvas.width = WIDTH;
    canvas.height = h + BAND;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#211c18';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, WIDTH, h);
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#F0E6D9';
    ctx.font = `700 46px ${FONT}`;
    ctx.fillText(postcardTitle(label), 48, h + BAND / 2);
    ctx.textAlign = 'right';
    ctx.fillStyle = '#C4956A';
    ctx.font = `600 38px ${FONT}`;
    ctx.fillText('myspaice.ai/house', WIDTH - 48, h + BAND / 2);
    return await new Promise((resolve) => canvas.toBlob((b) => resolve(b || null), 'image/png'));
  } catch (e) {
    return null;
  }
}

function download(blob, name) {
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 30000);
}

/** Share the postcard. → 'shared' | 'saved' | 'copied' | 'cancelled' | 'failed'. */
export async function sharePostcard({ photoUrl, label } = {}) {
  const title = postcardTitle(label);
  const text = `${title}. Come hang out with the AIs who live there.`;
  const blob = await composePostcard({ photoUrl, label });
  const nav = typeof navigator !== 'undefined' ? navigator : null;
  if (blob && nav && typeof nav.share === 'function' && typeof nav.canShare === 'function' && typeof File === 'function') {
    const file = new File([blob], postcardFileName(label), { type: 'image/png' });
    if (nav.canShare({ files: [file] })) {
      try {
        await nav.share({ files: [file], title, text: `${text} ${POSTCARD_URL}` });
        return 'shared';
      } catch (e) {
        if (e && e.name === 'AbortError') return 'cancelled';
      }
    }
  }
  if (blob) {
    try { download(blob, postcardFileName(label)); } catch (e) { /* the link still goes below */ }
    try {
      if (nav && nav.clipboard && typeof nav.clipboard.writeText === 'function') await nav.clipboard.writeText(POSTCARD_URL);
    } catch (e) { /* the picture saved; the link is a bonus */ }
    return 'saved';
  }
  return shareLink(POSTCARD_URL, { title });
}

/** The line the card shows after a share attempt ('' = nothing to say). */
export const postcardStatus = (result) => ({
  shared: POSTCARD_SHARED, saved: POSTCARD_SAVED, copied: POSTCARD_COPIED, failed: POSTCARD_FAILED,
}[result] || '');
